/**
 * DateRangeCalendar — viewport over a start/end range of any length.
 *
 * Desktop/tablet paint two months; phone paints one. Prev/next moves that
 * window. The selected start survives every page — November, December,
 * January. A tap in a later month is `onSelectDate`, same as a tap next
 * door. `current={startDate}` is forbidden here: that was the defect that
 * locked September and made long ranges impossible.
 *
 * Availability, changeover floors and min duration stay in the parent via
 * `isDateDisabled` / `onSelectDate`. This file does not invent holds.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ActivityIndicator, Platform, Text, View } from 'react-native'
import { useTranslation } from 'react-i18next'
import RNCalendar from 'react-native-calendars/src/calendar'
import type { DateData } from 'react-native-calendars/src/types'
import type { MarkingProps } from 'react-native-calendars/src/calendar/day/marking'
import { ChevronLeft, ChevronRight } from '@/components/ui/icons'
import { Pressable } from './Pressable'
import { useTheme } from '../../lib/hooks/use-theme'
import { useBreakpoint } from '../../lib/hooks/use-breakpoint'
import { useFontScale } from '../../lib/hooks/use-font-scale'
import { useSemanticColors, INTERACTION, RADIUS, SPACING } from '../../lib/theme/tokens'
import { COLORS, FONTS } from '../../lib/brand'
import {
  addCalendarDaysISO,
  addMonthsToMonthStart,
  clampDisplayedMonth,
  dateRangeCalendarUsesTwoMonths,
  datesBetweenExclusive,
  monthStartFromDate,
  monthToRevealAfterPickup,
  type DateRangeField,
} from '../../lib/utils/date-range-calendar'

export interface DateRangeCalendarProps {
  startDate: string | null
  endDate: string | null
  minDate: string
  maxDate?: string
  activeField: DateRangeField
  onSelectDate: (date: string) => void
  isDateDisabled: (date: string) => boolean
  firstAvailableStartDate?: string | null
  firstAvailableReturnDate?: string | null
  tomorrow?: string
  loading?: boolean
  loadingLabel?: string
  testID?: string
}

function monthTitle(monthStart: string, monthsCsv: string): string {
  const monthIndex = Number(monthStart.slice(5, 7)) - 1
  const year = monthStart.slice(0, 4)
  const names = monthsCsv.split(',')
  const name = names[monthIndex]?.trim() || monthStart.slice(5, 7)
  return `${name} ${year}`
}

export function DateRangeCalendar({
  startDate,
  endDate,
  minDate,
  maxDate,
  activeField,
  onSelectDate,
  isDateDisabled,
  firstAvailableStartDate,
  firstAvailableReturnDate,
  tomorrow,
  loading = false,
  loadingLabel,
  testID = 'date-range-calendar',
}: DateRangeCalendarProps) {
  const { t } = useTranslation()
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  const { box } = useFontScale()
  const { isDesktop, isTablet } = useBreakpoint()
  const usesTwoMonths = dateRangeCalendarUsesTwoMonths(isDesktop, isTablet)
  const horizon = maxDate ?? addCalendarDaysISO(minDate, 540)
  const [displayedMonth, setDisplayedMonth] = useState(() =>
    monthStartFromDate(startDate || minDate),
  )
  const lastRevealKey = useRef<string>('')

  useEffect(() => {
    if (!startDate) return
    const key = `${activeField}:${startDate}:${usesTwoMonths ? '2' : '1'}`
    if (lastRevealKey.current === key) return
    lastRevealKey.current = key
    if (activeField !== 'return') return
    setDisplayedMonth(
      clampDisplayedMonth(monthToRevealAfterPickup(startDate, usesTwoMonths), minDate, horizon),
    )
  }, [activeField, horizon, minDate, startDate, usesTwoMonths])

  const rightMonth = addMonthsToMonthStart(displayedMonth, 1)
  const canGoPrev = addMonthsToMonthStart(displayedMonth, -1) >= monthStartFromDate(minDate)
  const canGoNext =
    addMonthsToMonthStart(displayedMonth, usesTwoMonths ? 2 : 1) <= monthStartFromDate(horizon)

  const goMonth = useCallback(
    (delta: number) => {
      setDisplayedMonth((current) =>
        clampDisplayedMonth(addMonthsToMonthStart(current, delta), minDate, horizon),
      )
    },
    [horizon, minDate],
  )

  const markedDates = useMemo(() => {
    const marks: Record<string, MarkingProps> = {}
    if (startDate && endDate) {
      marks[startDate] = {
        startingDay: true,
        color: colors.textAccent,
        textColor: '#FFFFFF',
      }
      marks[endDate] = {
        endingDay: true,
        color: colors.textAccent,
        textColor: '#FFFFFF',
      }
      for (const date of datesBetweenExclusive(startDate, endDate)) {
        marks[date] = {
          color: `${colors.textAccent}33`,
          textColor: colors.textPrimary,
        }
      }
    } else if (startDate) {
      marks[startDate] = {
        startingDay: true,
        endingDay: true,
        color: colors.textAccent,
        textColor: '#FFFFFF',
      }
    }
    return marks
  }, [colors.textAccent, colors.textPrimary, endDate, startDate])

  const calendarTheme = useMemo(
    () => ({
      backgroundColor: 'transparent',
      calendarBackground: 'transparent',
      textSectionTitleColor: colors.textSecondary,
      selectedDayBackgroundColor: COLORS.coral.DEFAULT,
      selectedDayTextColor: '#FFFFFF',
      todayTextColor: COLORS.coral.DEFAULT,
      dayTextColor: colors.textPrimary,
      textDisabledColor: colors.textDisabled,
      arrowColor: COLORS.coral.DEFAULT,
      monthTextColor: colors.textPrimary,
      textDayFontFamily: FONTS.body,
      textMonthFontFamily: FONTS.bodyBold,
      textDayHeaderFontFamily: FONTS.body,
      textDayFontSize: 14,
      textMonthFontSize: 16,
      textDayHeaderFontSize: 12,
    }),
    [colors],
  )

  const renderDay = useCallback(
    ({ date, marking }: { date?: DateData; state?: string; marking?: MarkingProps }) => {
      if (!date) return <View style={{ width: 40, height: 40 }} />

      const dateString = date.dateString
      const isPickup = dateString === startDate
      const isReturn = dateString === endDate
      const isDisabled = isDateDisabled(dateString)
      const isRangeEdge = Boolean(
        marking?.startingDay || marking?.endingDay || isPickup || isReturn,
      )
      const backgroundColor = marking?.color ?? 'transparent'
      const dayTestID =
        activeField === 'pickup' && dateString === firstAvailableStartDate
          ? 'date-picker.first-available-start'
          : activeField === 'return' && dateString === firstAvailableReturnDate
            ? 'date-picker.first-available-return'
            : dateString === minDate
              ? 'date-picker.today'
              : dateString === tomorrow
                ? 'date-picker.tomorrow'
                : `date-picker.day-${dateString}`

      return (
        <Pressable
          onPress={() => onSelectDate(dateString)}
          disabled={false}
          accessibilityRole="button"
          accessibilityLabel={String(date.day)}
          accessibilityState={{ disabled: isDisabled, selected: isRangeEdge }}
          testID={dayTestID}
          style={{
            minWidth: box(INTERACTION.minTouchTarget),
            minHeight: box(INTERACTION.minTouchTarget),
            alignItems: 'center',
            justifyContent: 'center',
            opacity: isDisabled && !isRangeEdge ? 0.55 : 1,
          }}
        >
          <View
            style={{
              width: 34,
              height: 34,
              borderRadius: isRangeEdge ? 17 : RADIUS.sm,
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor,
              borderWidth: isDisabled && isRangeEdge ? 1 : 0,
              borderColor: isDisabled && isRangeEdge ? COLORS.coral.DEFAULT : 'transparent',
            }}
          >
            <Text
              style={{
                fontSize: 14,
                fontFamily: isRangeEdge || !isDisabled ? FONTS.bodyBold : FONTS.body,
                color:
                  marking?.textColor ?? (isDisabled ? colors.textDisabled : colors.textPrimary),
              }}
            >
              {date.day}
            </Text>
          </View>
        </Pressable>
      )
    },
    [
      activeField,
      box,
      colors.textDisabled,
      colors.textPrimary,
      endDate,
      firstAvailableReturnDate,
      firstAvailableStartDate,
      isDateDisabled,
      minDate,
      onSelectDate,
      startDate,
      tomorrow,
    ],
  )

  useEffect(() => {
    if (Platform.OS !== 'web' || typeof window === 'undefined') return undefined
    const onKeyDown = (event: { key: string; preventDefault: () => void }) => {
      if (event.key === 'PageUp') {
        if (!canGoPrev) return
        event.preventDefault()
        goMonth(-1)
        return
      }
      if (event.key === 'PageDown') {
        if (!canGoNext) return
        event.preventDefault()
        goMonth(1)
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [canGoNext, canGoPrev, goMonth])

  const monthsCsv = t('calendar.months')
  const monthStarts = usesTwoMonths ? [displayedMonth, rightMonth] : [displayedMonth]

  return (
    <View
      testID={testID}
      style={{
        backgroundColor: colors.surface,
        borderRadius: RADIUS.lg,
        borderWidth: 1,
        borderColor: colors.border,
        paddingVertical: SPACING.sm,
        overflow: 'hidden',
      }}
    >
      <View
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          minHeight: box(INTERACTION.minTouchTarget),
          paddingHorizontal: SPACING.sm,
          gap: SPACING.sm,
        }}
      >
        <Pressable
          onPress={() => goMonth(-1)}
          disabled={!canGoPrev}
          accessibilityRole="button"
          accessibilityLabel={t('calendar.previous_month')}
          testID={`${testID}.header.leftArrow`}
          style={{
            width: INTERACTION.minTouchTarget,
            height: INTERACTION.minTouchTarget,
            alignItems: 'center',
            justifyContent: 'center',
            opacity: canGoPrev ? 1 : 0.35,
          }}
        >
          <ChevronLeft size={22} color={COLORS.coral.DEFAULT} />
        </Pressable>
        <View
          style={{
            flex: 1,
            flexDirection: 'row',
            gap: SPACING.md,
          }}
        >
          {monthStarts.map((monthStart) => (
            <Text
              key={monthStart}
              style={{
                flex: 1,
                fontSize: 16,
                fontFamily: FONTS.bodyBold,
                color: colors.textPrimary,
                textAlign: 'center',
              }}
              numberOfLines={1}
            >
              {monthTitle(monthStart, monthsCsv)}
            </Text>
          ))}
        </View>
        <Pressable
          onPress={() => goMonth(1)}
          disabled={!canGoNext}
          accessibilityRole="button"
          accessibilityLabel={t('calendar.next_month')}
          testID={`${testID}.header.rightArrow`}
          style={{
            width: INTERACTION.minTouchTarget,
            height: INTERACTION.minTouchTarget,
            alignItems: 'center',
            justifyContent: 'center',
            opacity: canGoNext ? 1 : 0.35,
          }}
        >
          <ChevronRight size={22} color={COLORS.coral.DEFAULT} />
        </Pressable>
      </View>

      <View
        style={{
          flexDirection: usesTwoMonths ? 'row' : 'column',
          gap: SPACING.sm,
        }}
      >
        {monthStarts.map((monthStart) => (
          <View key={monthStart} style={{ flex: 1, minWidth: 0 }}>
            <RNCalendar
              key={monthStart}
              current={monthStart}
              minDate={minDate}
              maxDate={horizon}
              firstDay={1}
              hideArrows
              enableSwipeMonths={false}
              hideExtraDays={usesTwoMonths}
              markingType="period"
              markedDates={markedDates}
              theme={calendarTheme}
              onDayPress={(day) => onSelectDate(day.dateString)}
              dayComponent={renderDay}
              renderHeader={() => null}
              testID={`${testID}.month-${monthStart.slice(0, 7)}`}
            />
          </View>
        ))}
      </View>

      {loading ? (
        <View
          accessible
          accessibilityLabel={loadingLabel}
          testID="booking.why-disabled.availability-loading"
          style={{
            position: 'absolute',
            top: 0,
            right: 0,
            bottom: 0,
            left: 0,
            alignItems: 'center',
            justifyContent: 'center',
            gap: SPACING.sm,
            backgroundColor: colors.surface,
          }}
        >
          <ActivityIndicator size="small" color={colors.textSecondary} />
          <Text
            style={{
              fontFamily: FONTS.body,
              fontSize: 14,
              color: colors.textSecondary,
              textAlign: 'center',
              paddingHorizontal: SPACING.lg,
              marginTop: SPACING.sm,
            }}
          >
            {loadingLabel}
          </Text>
        </View>
      ) : null}
    </View>
  )
}
